import {useParsed} from '@refinedev/core';
import {useBoolean} from 'minimal-shared/hooks';
import {isActiveLink, isExternalLink} from 'minimal-shared/utils';
import {useCallback, useEffect, useRef} from 'react';

import {NavCollapse, NavLi, NavUl} from '../components';
import {navSectionClasses} from '../styles';
import type {NavListProps, NavSubListProps} from '../types';
import {NavItem} from './nav-item';

// ----------------------------------------------------------------------

export function NavList({data, depth, render, slotProps, currentRole, enabledRootRedirect}: NavListProps) {
  const {pathname} = useParsed();
  const navItemRef = useRef<HTMLButtonElement | null>(null);

  const isActive = isActiveLink(pathname!, data.path, !!data.children);

  const {value: open, onFalse: onClose, onToggle} = useBoolean(isActive);

  useEffect(() => {
    if (!isActive) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle();
    }
  }, [data.children, onToggle]);

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      open={open}
      active={isActive}
      disabled={data.disabled}
      // options
      depth={depth}
      render={render}
      hasChild={!!data.children}
      externalLink={isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onClick={handleToggleMenu}
    />
  );

  const renderCollapse = () =>
    !!data.children && (
      <NavCollapse mountOnEnter unmountOnExit depth={depth} in={open} data-group={data.title}>
        <NavSubList
          data={data.children}
          render={render}
          depth={depth}
          slotProps={slotProps}
          currentRole={currentRole}
          enabledRootRedirect={enabledRootRedirect}
        />
      </NavCollapse>
    );

  // Hidden item by role
  if (data.roles && currentRole && !data.roles.includes(currentRole)) {
    return null;
  }

  return (
    <NavLi
      disabled={data.disabled}
      sx={{
        ...(!!data.children && {
          [`& .${navSectionClasses.li}`]: {'&:first-of-type': {mt: 'var(--nav-item-gap)'}},
        }),
      }}
    >
      {renderNavItem()}
      {renderCollapse()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({data, render, depth = 0, slotProps, currentRole, enabledRootRedirect}: NavSubListProps) {
  return (
    <NavUl sx={{gap: 'var(--nav-item-gap)'}}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          currentRole={currentRole}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );
}
